import * as React from "react";
import {useCallback, useEffect} from "react";
import {Navbar} from "react-bootstrap";
import {hasSessionId, navigate, Routes} from "../utils/router";
import {isSuperAdmin, isUserAdmin} from "../utils/commons.ts";
import {UserProvider} from "../types/adminstate";
import {useAuth0} from "@auth0/auth0-react";
import {
    KButton,
    KBUTTON_SIZE,
    KBUTTON_VARIANT,
    KNavbar,
    KNavBrandFixed,
    KNavDropdown,
    KNavDropdownItem,
    KNavLangSelector,
    KNavLink,
    noop,
    Translate
} from "@kopjra/uikit";
import {useAppDispatch, useAppSelector} from "../utils/store.ts";
import {useGetStatsQuery, useGetUserInfo, useLogoutMutation} from "../api/user.ts";
import {VERSION} from "../utils/constants.ts";
import {Feature, hasFeature} from "../utils/features.ts";
import {useLocation} from "react-router-dom";
import {clearReferrerRoute} from "../slices/global.ts";

export const Toolbar: React.FC = () => {
    const location = useLocation();
    const {logout, user} = useAuth0();
    const isAdminSession: boolean = location.pathname.startsWith(Routes.routeAdmin_sessions);

    const {data: userInfo} = useGetUserInfo();
    const {data: stats, refetch} = useGetStatsQuery();

    const [triggerLogout] = useLogoutMutation();

    const {referrerRoute} = useAppSelector(state => state.globalSlice);
    const dispatch = useAppDispatch();

    const onBack = useCallback(() => {
        if (referrerRoute) {
            navigate(referrerRoute);
            dispatch(clearReferrerRoute());
        } else {
            navigate(-1);
        }
    }, [referrerRoute]);

    useEffect(() => {
        refetch();
    }, [userInfo]);

    return Routes.inNavigate() ? null : (
        <KNavbar dark={Routes.inNew_session() || Routes.inNavigate()}
                 brand={(hasSessionId(location) || isAdminSession) ? (
                     <Navbar.Brand><i className="fal fa-lg fa-arrow-left" style={{cursor: "pointer", padding: "15px 0"}} onClick={() => onBack()}/></Navbar.Brand>
                 ) : (
                     <KNavBrandFixed appName={"Web Forensics"} iconPartial={"web-forensics"} dark={Routes.inNew_session() || Routes.inNavigate()}/>
                 )}
                 rightMost={<>
                     <KNavLangSelector alignRight={true}/>
                     {(userInfo && stats && (stats.infinite || stats.remaining > 0)) ? (
                         !(Routes.inNew_session() || Routes.inNavigate()) ? (
                             <KButton text={<><i className="fal fa-lg fa-plus"/>&nbsp;&nbsp;<Translate i18nKey="bar.createbutton"/></>}
                                      variant={KBUTTON_VARIANT.success}
                                      onClick={() => Routes.goNew_session()}
                                      size={KBUTTON_SIZE.xs}
                             />
                         ) : (
                             <KButton text={<><i className="fal fa-lg fa-times"/>&nbsp;&nbsp;<Translate i18nKey="bar.cancelbutton"/></>}
                                      variant={KBUTTON_VARIANT.secondary}
                                      onClick={() => Routes.goSessions()}
                                      size={KBUTTON_SIZE.xs}
                             />
                         )
                     ) : null}
                 </>}
                 statusPageComponentIds={["963l1kbx2v7b"]}
        >
            {userInfo ? (
                <>
                    <KNavLink active={location.pathname.startsWith(Routes.routeSessions) && !hasSessionId(location)} onClick={() => Routes.goSessions()} element={<Translate i18nKey="bar.sessions"/>}/>
                    <KNavLink active={location.pathname.startsWith(Routes.routeMy_sessions)} onClick={() => Routes.goMy_sessions()} element={<Translate i18nKey="bar.mysessions"/>}/>
                    {isUserAdmin(userInfo) ? (
                        <KNavLink active={location.pathname.startsWith(Routes.routeAdmin)} onClick={() => Routes.goAdmin()} element={
                            <>
                                {isSuperAdmin(userInfo) ? <i className="fal fa-unlock-alt" style={{marginRight: 2}}/> : <i className="fal fa-lock-alt" style={{marginRight: 2}}/>}
                                <Translate i18nKey="bar.admin"/>
                            </>
                        }/>
                    ) : null}
                </>
            ) : null}
            <KNavDropdown id={"user.profile-dropdown"} title={<Translate i18nKey="bar.account"/>} version={VERSION}>
                {!userInfo ? (
                    <>
                        <div style={{padding: 20, width: "100%"}}>
                            <KButton text={<span><i className="fal fag fa-sign-in"/> <Translate i18nKey="bar.login"/></span>} variant={KBUTTON_VARIANT.success} size={KBUTTON_SIZE.sm}
                                     onClick={() => hasFeature(Feature.AUTH0) ? Routes.goSessions() : Routes.goLogin()} fill={true}/>
                        </div>
                        <KNavDropdownItem onClick={() => noop()}>
                            <Translate i18nKey="bar.support"/>
                        </KNavDropdownItem>
                    </>
                ) : (
                    <>
                        <KNavDropdownItem onClick={() => Routes.goUser_account()}>
                            <Translate i18nKey="bar.profile"/>
                        </KNavDropdownItem>
                        <KNavDropdownItem onClick={() => {
                            if (user?.partner === "opendotcom") {
                                window.open("https://www.opendotcom.it/tts/centro-assistenza.aspx?servId=1114", "_blank");
                            } else {
                                window.open("https://kopjra.atlassian.net/servicedesk/customer/portal/5", "_blank");
                            }
                        }}>
                            <Translate i18nKey="bar.support"/>
                        </KNavDropdownItem>
                        <KNavDropdownItem onClick={async () => {
                            await triggerLogout(userInfo.provider !== UserProvider.KOPJRA).unwrap();
                            if (userInfo.provider === UserProvider.KOPJRA) {
                                await logout({logoutParams: {returnTo: "https://webforensics.kopjra.com"}});
                            }
                        }}>
                            <Translate i18nKey="bar.logout"/>
                        </KNavDropdownItem>
                    </>
                )}
            </KNavDropdown>
        </KNavbar>
    );
};
